import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import clsx from 'clsx'
import ResponsiveRangedGauge from '../../../../nivo/RangedGauge/ResponsiveRangedGauge'
import GaugeLoadingState from '../../../../charts/echarts/LoadingState/GaugeLoadingState'
import { GaugeEmptyState } from '../../../../charts/echarts/EmptyState/GaugeEmptyState'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import useReportComponentAccessors from '../../inspector/useReportComponentAccessors'
import { buildPropAccessors } from '../../inspector/buildPropAccessors'

function PipelineGauge ({
  pipeline,
  variables,
  rangeAccessor,
  valueAccessor,
  titleAccessor,
  title,
  valueFormat,
  rangeColors,
  markerColor,
  className,
  style,
  height,
  margin,
  ...restProps
}) {
  const { data, isLoading, inspectorId } = usePipelineDataContextWithVariables(pipeline, variables)

  const reportedAccessors = useMemo(
    () => buildPropAccessors({ rangeAccessor, valueAccessor, titleAccessor }),
    [rangeAccessor, valueAccessor, titleAccessor]
  )
  useReportComponentAccessors({
    inspectorId,
    component: 'DDPGauge',
    label: typeof pipeline === 'string' ? pipeline : null,
    accessors: reportedAccessors
  })

  const { ranges, subTitle, marker } = useMemo(() => {
    const ranges = (get(data, rangeAccessor) || []).map((range, index) => ({
      ...range,
      color: range.color || (rangeColors || []).at(index)
    }))
    const validRanges = ranges.some(range => range.start === range.end) ? null : ranges
    if (!validRanges) {
      return []
    }
    return ({
      value: get(data, valueAccessor),
      ranges,
      subTitle: titleAccessor ? get(data, titleAccessor) : title,
      marker: {
        id: 'Value',
        value: get(data, valueAccessor),
        format: valueFormat,
        color: markerColor
      }
    })
  }, [data, valueAccessor, rangeAccessor, titleAccessor, valueFormat, title, markerColor, rangeColors])

  if (isLoading) {
    return <GaugeLoadingState style={style} className={clsx(className)} height={height} />
  }

  if (!data || !ranges) {
    return (
      <GaugeEmptyState
        style={style}
        className={clsx(className)}
        height={height}
        title='--'
        subtitle={subTitle ?? 'There is no data to display'}
      />
    )
  }

  return (
    <div className={className} style={{ ...(style || {}), height }}>
      <ResponsiveRangedGauge
        height={height}
        marker={marker}
        ranges={ranges}
        subTitle={subTitle}
        valueFormat={valueFormat}
        margin={margin}
        {...restProps}
      />
    </div>
  )
}

PipelineGauge.propTypes = {
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  rangeAccessor: PropTypes.string,
  valueAccessor: PropTypes.string,
  titleAccessor: PropTypes.string,
  title: PropTypes.string,
  valueFormat: PropTypes.string,
  rangeColors: PropTypes.arrayOf(PropTypes.string),
  markerColor: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  style: PropTypes.object,
  margin: PropTypes.object
}

PipelineGauge.defaultProps = {
  valueFormat: 'percentage',
  markerColor: '#0D97CD',
  rangeColors: ['#7030A0', '#92D051', '#386092'],
  margin: { top: 0, right: 10, left: 10, bottom: 30 }
}

export default PipelineGauge
